import { now } from '@seen/core/utils'
import { useConfirmWithAnnotationStore } from '#src/hooks/useConfirmWithAnnotationStore.ts'

interface ConfirmWithDateAndAnnotationOptions {
  /** The title of the confirmation dialog */
  title: string
  /** The description text for the confirmation dialog */
  description: string
  /** Callback function that receives the note text and selected date */
  onConfirm: (note: string, date: string) => void
  /** Initial date to show in the date picker (defaults to current date) */
  defaultDate?: string
  /** Whether to use rich text editor for the note (defaults to false) */
  isRichText?: boolean
}

interface ConfirmWithDateAndAnnotationResult {
  /** Function to open the confirmation dialog with date and annotation inputs */
  open: (options: ConfirmWithDateAndAnnotationOptions) => void
}

/**
 * Hook that provides a function to open a confirmation dialog with date and annotation inputs
 * @returns Object with an open function to show the dialog
 */
export function useConfirmWithDateAndAnnotation(): ConfirmWithDateAndAnnotationResult {
  const { open } = useConfirmWithAnnotationStore()

  /**
   * Opens a confirmation dialog with date and annotation inputs
   * @param options - Configuration options for the dialog
   */
  const openWithDateAndAnnotation = (
    options: ConfirmWithDateAndAnnotationOptions
  ): void => {
    const {
      title,
      description,
      onConfirm,
      defaultDate = now(),
      isRichText = false,
    } = options

    open({
      title,
      description,
      onConfirm: (note: string, date?: string): void => {
        if (date) {
          onConfirm(note, date)
        } else {
          onConfirm(note, defaultDate)
        }
      },
      isRichText,
      includeDate: true,
      defaultDate,
    })
  }

  return { open: openWithDateAndAnnotation }
}
